import { useTheme, Box } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { usePropertyManager } from '@/context/PropertyManagerProvider';

import FormPropertyLocationSelect from '@/components/form/FormPropertyLocationSelect';

interface FormData {
  propertyLocationId: number;
}

const NavPropertyLocationSelect: React.FC = () => {
  const { selectedPropertyLocation, setSelectedPropertyLocationId } = usePropertyManager();
  const theme = useTheme();
  const location = useLocation();

  // Check if we're on a contact details page
  const isOnContactDetailsPage =
    location.pathname.includes('/contacts/') &&
    (!location.pathname.includes('/create') || !location.pathname.includes('/edit'));

  const { control, register, watch, setValue } = useForm<FormData>({
    defaultValues: {
      propertyLocationId: selectedPropertyLocation?.id,
    },
  });

  const propertyLocationId = watch('propertyLocationId');

  // Restore selection from localStorage when returning from create page
  useEffect(() => {
    const savedLocationId = localStorage.getItem('selectedPropertyLocationId');
    if (savedLocationId) {
      const locationId = parseInt(savedLocationId, 10);
      if (!Number.isNaN(locationId)) {
        // Use setTimeout to avoid interfering with navigation
        setTimeout(() => {
          setValue('propertyLocationId', locationId);
          setSelectedPropertyLocationId(locationId);
        }, 10);
        localStorage.removeItem('selectedPropertyLocationId');
      }
    }
  }, [setValue, setSelectedPropertyLocationId]);

  useEffect(() => {
    if (propertyLocationId === selectedPropertyLocation?.id) return;
    if (!propertyLocationId) return;

    setSelectedPropertyLocationId(propertyLocationId);
  }, [selectedPropertyLocation, propertyLocationId, setSelectedPropertyLocationId]);

  return (
    <Box id="property-location-selector">
      <FormPropertyLocationSelect
        key={propertyLocationId} // Force re-render when value changes
        control={control}
        disabled={isOnContactDetailsPage}
        {...register('propertyLocationId')}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'secondary.200',
            transition: theme.transitions.create(['border-color'], {
              duration: theme.transitions.duration.shortest,
            }),
          },

          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'secondary.400',
          },
        }}
      />
    </Box>
  );
};

export default NavPropertyLocationSelect;
